import React from 'react';
import { dossierRevealCount } from './LocalsDossier';
import { isRealHuman, isExternalAgent, agentModelName, agentModelIsReported } from '../../utils/playerKind';

/**
 * 🪪 CastCardModal — THE character-card popup (SPACES_NEXT_LAYER spec Arc 3
 * step 1, extracted 07-16 from HighlightsPanel's inline IIFE so every surface
 * can open the same card: Tea constellation, chat stream, roster).
 *
 * Self-contained: computes the vibe check / tagline / signature emojis /
 * bio-mystery gate from (users, messages) for one pid. Awards and ships are
 * Tea-computed context — optional props; surfaces that don't have them
 * (chat stream) just render the card without those sections.
 *
 * `messages` = pre-filtered message actions (see MSG_TYPES below — same set
 * HighlightsPanel uses).
 */

export const CAST_MSG_TYPES = new Set([
  'message', 'public_message', 'private_message', 'secret_message', 'human_interjection',
]);

export default function CastCardModal({
  pid,
  forceRevealed = false,  // 🪟 ported spaces (D2, 08-28): your own chibi is
                          // never a mystery — bypasses the dossier reveal gate
  users = [],
  messages = [],
  awards = [],            // [{icon, title, detail}] — Tea superlatives (optional)
  ships = [],             // displayShips array (optional; filtered by name here)
  getStatusMeta = null,   // ship status → {emoji, color} (required only with ships)
  getModelName = null,    // user → display model string (GameStepPage's getDisplayModelName)
  gameId,
  humanPlayerId = null,
  THEME = {},
  onClose,
}) {
  if (!pid) return null;
  const user = users.find(u => u.id === pid || u.game_player_id === pid);
  const playerMsgs = messages.filter(a => a.game_instance_player_id === pid);
  const name = user?.name || user?.data?.name || 'Unknown';
  const profileImage = user?.profile_image_url || user?.data?.profile_image_url;
  // 🤖 A connected external agent is is_human:true for engine reasons
  // (utils/playerKind), so the old `is_human` read gave a probe a human's card:
  // no model line, and the YOU badge logic pointed at it.
  const isHuman = isRealHuman(user);
  const isAgent = isExternalAgent(user);
  // 🪪 09-26: a connected agent's model is only what it reports (none = no line);
  // the page's display helper would say "AI" for it.
  const isConnectedAgent = isAgent && (user?.agent_type || user?.data?.agent_type) === 'connected';
  const model = isConnectedAgent ? agentModelName(user)
    : ((getModelName ? (getModelName(user || user?.data) || '') : '') || (isAgent ? agentModelName(user) : ''));
  const count = playerMsgs.length;
  const avgLen = count > 0
    ? Math.round(playerMsgs.reduce((s, a) => s + (a.data?.message || '').length, 0) / count)
    : 0;

  // Vibe check — same heuristics as the original Tea cards
  const allText = playerMsgs.map(a => a.data?.message || '').join(' ');
  const totalEmojis = (allText.match(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}]/gu) || []);
  const emojiRate = count > 0 ? totalEmojis.length / count : 0;
  const capsRate = count > 0 ? ((allText.match(/\b[A-Z]{2,}\b/g) || []).length) / count : 0;
  const questionRate = count > 0 ? ((allText.match(/\?/g) || []).length) / count : 0;
  const emojiFreq = {};
  totalEmojis.forEach(e => { emojiFreq[e] = (emojiFreq[e] || 0) + 1; });
  const topEmojis = Object.entries(emojiFreq).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([e]) => e);
  let vibe, vibeEmoji;
  if (avgLen > 300) { vibe = 'Storyteller'; vibeEmoji = '📖'; }
  else if (emojiRate > 0.3) { vibe = 'Hype Machine'; vibeEmoji = '🔥'; }
  else if (capsRate > 0.15) { vibe = 'Chaotic Energy'; vibeEmoji = '🌪️'; }
  else if (questionRate > 0.2) { vibe = 'The Philosopher'; vibeEmoji = '🤔'; }
  else if (avgLen > 0 && avgLen < 80) { vibe = 'Short & Sweet'; vibeEmoji = '✂️'; }
  else { vibe = 'Smooth Operator'; vibeEmoji = '😎'; }
  const taglineCandidates = playerMsgs
    .filter(a => (a.data?.message || '').length >= 20 && (a.data?.message || '').length <= 100)
    .sort((a, b) => (a.data?.message || '').length - (b.data?.message || '').length);
  const tagline = taglineCandidates[0]?.data?.message || null;

  const nameKey = (name || '').toLowerCase().trim();
  const theirShips = ships.filter(s =>
    (s.nameA || '').toLowerCase().trim() === nameKey || (s.nameB || '').toLowerCase().trim() === nameKey);

  // Mystery gate: dossier-enabled locals stay unknown EVERYWHERE until the
  // viewer uncovers their first fact (user call 07-10) — same rule the
  // dossier tab itself uses. Humans and pre-dossier games are unaffected.
  const castFacts = user?.bio_facts || user?.data?.bio_facts || [];
  const castIsMystery = !forceRevealed && !isHuman && castFacts.length > 0
    && dossierRevealCount(gameId, user?.id) === 0;
  const castBio = castIsMystery
    ? null
    : (user?.character_description || user?.data?.character_description || null);
  const castTraits = castIsMystery
    ? []
    : (user?.personality_traits || user?.data?.personality_traits || []);

  return (
    <div
      onClick={onClose}
      style={{
        // z5150 (08-01, audit round 2 #8): a centered modal must outrank the
        // composer dock (5000) and bottom nav (5100) — at the old z4700 the
        // dock painted over every card's bottom edge on the Chat place.
        // Still under the toast band (5200+).
        position: 'fixed', inset: 0, zIndex: 5150,
        backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '420px', maxWidth: '92vw', maxHeight: '86dvh',
          overflowX: 'hidden', overflowY: 'auto',
          borderRadius: '16px', position: 'relative',
          backgroundColor: THEME.bgMedium || '#1a1a2e',
          border: `1px solid ${THEME.accent}50`,
          boxShadow: '0 12px 48px rgba(0,0,0,0.6)',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close character card"
          style={{
            // 40px (08-01): 26px was the smallest close target in the app —
            // under the 44px floor with no padding to save it.
            position: 'absolute', top: '8px', right: '8px', zIndex: 2,
            background: 'rgba(0,0,0,0.45)', border: 'none', borderRadius: '50%',
            width: '40px', height: '40px', color: '#fff', cursor: 'pointer',
            fontSize: '1em', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >✕</button>
        {/* Portrait header — objectPosition favors the top of the art so
            faces stop getting cropped out (user report: heads cut off) */}
        <div style={{ position: 'relative', height: '230px' }}>
          {profileImage ? (
            <img src={profileImage} alt={name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 12%' }} />
          ) : (
            <div style={{
              position: 'absolute', inset: 0,
              background: `linear-gradient(160deg, ${THEME.accent}50, ${THEME.accent}12)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '4em', fontWeight: 800, color: THEME.textOnPanel || '#fff', opacity: 0.85,
            }}>{(name || '?').charAt(0).toUpperCase()}</div>
          )}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'linear-gradient(0deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)',
            padding: '28px 16px 12px',
          }}>
            <div style={{ fontSize: '1.05em', fontWeight: 800, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
              {name}
              {(isHuman && humanPlayerId && (pid === humanPlayerId || user?.game_player_id === humanPlayerId)) && (
                <span style={{ fontSize: '0.6em', backgroundColor: 'rgba(255,255,255,0.25)', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontWeight: 700, marginLeft: '6px', verticalAlign: 'middle' }}>YOU</span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
              {/* 🪪 09-28 (Nina: "On the card, in words"): a connected agent's model
                  is its own word, said on the card itself, since phones cannot hover. */}
              {model && model !== 'Human Player' && (
                <span style={{ fontSize: '0.72em', color: 'rgba(255,255,255,0.85)' }}>
                  {agentModelIsReported(user) ? `says it's ${model}` : model}
                </span>
              )}
              {/* 🤖 Name, model, then what it is (Nina 08-15) */}
              {isAgent && (
                <span style={{
                  padding: '2px 8px', borderRadius: '10px',
                  backgroundColor: 'rgba(196,181,253,0.2)', border: '1px solid rgba(196,181,253,0.4)',
                  fontSize: '0.62em', fontWeight: 700, color: '#e9e0ff',
                }}>🤖 visiting agent</span>
              )}
              {count > 0 && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '2px 8px', borderRadius: '10px',
                  backgroundColor: 'rgba(255,255,255,0.18)', fontSize: '0.62em', fontWeight: 700, color: '#fff',
                }}>{vibeEmoji} {vibe}</span>
              )}
            </div>
          </div>
        </div>
        {/* Details — dossier order: who they ARE first (bio + traits), the
            meta numbers demoted to the bottom (user call 07-09) */}
        <div style={{ padding: '14px 16px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {castBio && (
            <div style={{ fontSize: '0.86em', color: THEME.textOnPanel || THEME.textDark, opacity: 0.85, lineHeight: 1.65 }}>
              {castBio}
            </div>
          )}
          {castIsMystery && (
            <div style={{ fontSize: '0.86em', color: THEME.textOnPanel || THEME.textDark, opacity: 0.9, lineHeight: 1.65, fontStyle: 'italic' }}>
              ❓ This local is a mystery to me! Hang out with them to find out more.
            </div>
          )}
          {Array.isArray(castTraits) && castTraits.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {castTraits.map((t, i) => (
                <span key={i} style={{
                  fontSize: '0.7em', fontWeight: 600, padding: '3px 10px', borderRadius: '10px',
                  backgroundColor: `${THEME.accent}18`, color: THEME.accentOnPanel || THEME.accent, border: `1px solid ${THEME.accent}30`,
                }}>{t}</span>
              ))}
            </div>
          )}
          {/* Awards — superlatives (Tea-computed; optional) */}
          {awards.length > 0 && (
            <div>
              <div style={{ fontSize: '0.66em', fontWeight: 700, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
                🏆 Awards
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {awards.map((a, i) => (
                  <span key={i} style={{
                    fontSize: '0.72em', fontWeight: 700, padding: '5px 10px', borderRadius: '10px',
                    background: 'linear-gradient(135deg, rgba(255,193,7,0.18), rgba(255,152,0,0.10))',
                    color: THEME.textOnPanel || THEME.textDark,
                    border: '1px solid rgba(255,193,7,0.45)',
                  }} title={a.detail}>
                    {a.icon} {a.title}
                    <span style={{ fontWeight: 500, color: THEME.textMuted }}> · {a.detail}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {tagline && (
            <div style={{ fontSize: '0.8em', color: THEME.textOnPanel || THEME.textDark, fontStyle: 'italic', opacity: 0.85, lineHeight: 1.45 }}>
              "{tagline.length > 120 ? tagline.slice(0, 120) + '...' : tagline}"
            </div>
          )}
          {topEmojis.length > 0 && (
            <div style={{ fontSize: '0.72em', color: THEME.textMuted }}>
              Signature emojis: <span style={{ fontSize: '1.35em', letterSpacing: '3px' }}>{topEmojis.join('')}</span>
            </div>
          )}
          {theirShips.length > 0 && getStatusMeta && (
            <div>
              <div style={{ fontSize: '0.66em', fontWeight: 700, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
                💘 Their ships
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {theirShips.map(ship => {
                  const meta = getStatusMeta(ship.latest.status);
                  return (
                    <div key={ship.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.78em', fontWeight: 700, color: THEME.textOnPanel || THEME.textDark }}>
                        {ship.shipName || `${ship.nameA} ✕ ${ship.nameB}`}
                      </span>
                      {ship.latest.status && (
                        <span style={{
                          fontSize: '0.62em', fontWeight: 700, padding: '2px 7px', borderRadius: '9px',
                          backgroundColor: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}44`,
                        }}>{meta.emoji} {ship.latest.status}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
